import { DateTime } from 'luxon'
import { Area, AreaDto, areaFromDto, AreaID } from '../../areas/models/area'
import { EmployeeShiftOffer, EmployeeShiftOfferDto, employeeShiftOfferFromDto } from '../../employee_shift/models/employeeShiftOffer'
import { Employee, EmployeeID, EmployeeJson } from '../../employees/models/employee'
import { Skill, SkillJson } from '../../skills/models/skill'

declare const ShiftIDSymbol: unique symbol
export type ShiftID = string & { [ShiftIDSymbol]: never }

export type ShiftPublishedFlag = boolean & { readonly __brand: unique symbol }
export type ShiftOpenFlag = boolean & { readonly __brand: unique symbol }
export type ShiftApprovalRequiredFlag = boolean & { readonly __brand: unique symbol }

export enum ShiftStatus {
    ACTIVE = 'active',
    CANCELLED = 'cancelled',
}

export function getShiftStatus(status: number): ShiftStatus {
    switch (status) {
        case 1:
            return ShiftStatus.ACTIVE
        case 0:
            return ShiftStatus.CANCELLED
        default:
            throw new Error('Invalid shift status')
    }
}

export interface ShiftDraft {
    startDate: DateTime
    endDate: DateTime
    areaId: AreaID
}

type ShiftStatusLabel = 'active' | 'cancelled'

type ShiftAreaRelationDto = {
    data: AreaDto
} | null

type EmployeeShiftRelationDto = {
    data: EmployeeShiftOfferDto
} | null

type EmployeeShiftOffersRelationsDto = {
    data: EmployeeShiftOfferDto[]
} | null

type ShiftSkillsRelationsDto = {
    data: SkillJson[]
} | null

type ShiftCreatorRelationDto = {
    data: EmployeeJson
} | null

export interface ShiftDto {
    id: string

    status: string
    published: boolean
    locked: boolean

    start_date: string
    end_date: string

    approval_required: boolean

    open: boolean

    queue: boolean

    note: string

    rate_amount?: number
    rate_currency?: string

    area_id: string
    area?: ShiftAreaRelationDto

    roster_employee_id: string | null
    roster_employee_offer?: EmployeeShiftRelationDto

    offers: EmployeeShiftOffersRelationsDto

    skills?: ShiftSkillsRelationsDto

    has_potential_offers?: boolean

    has_applied_offers?: boolean

    creator?: ShiftCreatorRelationDto

    created_at?: string | null
    updated_at?: string | null

    reminded_at?: string | null

    permissions: { can_manage: boolean }
}

export interface Shift {
    id: ShiftID

    status: ShiftStatusLabel
    published: ShiftPublishedFlag
    locked: boolean

    startDate: DateTime
    endDate: DateTime
    duration: Duration

    approvalRequired: ShiftApprovalRequiredFlag

    open: ShiftOpenFlag

    queue: boolean

    note: string

    rateAmount?: number
    rateCurrency?: string

    areaId: AreaID
    area?: Area

    rosterEmployeeId: EmployeeID | null
    rosterEmployeeOffer?: EmployeeShiftOffer | null

    skills?: Skill[]

    hasPotentialOffers?: boolean

    hasAppliedOffers?: boolean

    creator?: Employee | null

    createdAt?: DateTime | null
    updatedAt?: DateTime | null

    remindedAt?: DateTime | null

    permissions: { canManage: boolean }
}

export const createShift = (
    params: {
        id: ShiftID

        status: ShiftStatusLabel
        published: boolean
        locked: boolean

        startDate: DateTime
        endDate: DateTime

        approvalRequired: boolean

        open: boolean

        queue: boolean

        note: string

        rateAmount?: number
        rateCurrency?: string

        areaId: AreaID
        area?: Area

        rosterEmployeeId: EmployeeID | null
        rosterEmployeeOffer: EmployeeShiftOffer | null

        skills?: Skill[]

        hasPotentialOffers?: boolean

        hasAppliedOffers?: boolean

        creator?: Employee | null

        createdAt?: DateTime | null
        updatedAt?: DateTime | null

        remindedAt?: DateTime | null

        permissions: { canManage: boolean }
    }
): Shift => ({
    id: params.id,

    status: params.status,
    published: params.published as ShiftPublishedFlag,
    locked: params.locked,

    startDate: params.startDate,
    endDate: params.endDate,
    duration: params.endDate.diff(params.startDate, 'hours'),

    approvalRequired: params.approvalRequired as ShiftApprovalRequiredFlag,

    open: params.open as ShiftOpenFlag,

    queue: params.queue,

    note: params.note,

    rateAmount: params.rateAmount,
    rateCurrency: params.rateCurrency,

    areaId: params.areaId,
    area: params.area,

    rosterEmployeeId: params.rosterEmployeeId,
    rosterEmployeeOffer: params.rosterEmployeeOffer,

    skills: params.skills,

    hasPotentialOffers: params.hasPotentialOffers,

    hasAppliedOffers: params.hasAppliedOffers,

    creator: params.creator,

    createdAt: params.createdAt,
    updatedAt: params.updatedAt,

    remindedAt: params.remindedAt,

    permissions: params.permissions,
})

export const shiftFromDto = (dto: ShiftDto): Shift => (createShift({
    id: dto.id as ShiftID,

    status: dto.status as ShiftStatusLabel,
    published: dto.published,
    locked: dto.locked,

    startDate: DateTime.fromISO(dto.start_date),
    endDate: DateTime.fromISO(dto.end_date),

    approvalRequired: dto.approval_required,

    open: dto.open,

    queue: dto.queue,

    note: dto.note,

    rateAmount: dto.rate_amount,
    rateCurrency: dto.rate_currency,

    areaId: dto.area_id as AreaID,
    area: dto.area?.data ? areaFromDto(dto.area.data) : undefined,

    rosterEmployeeId: dto.roster_employee_id as EmployeeID | null,
    rosterEmployeeOffer: dto.roster_employee_offer?.data ? employeeShiftOfferFromDto(dto.roster_employee_offer.data) : null,

    skills: dto.skills?.data ? dto.skills.data.map(Skill.fromJson) : undefined,

    hasPotentialOffers: dto.has_potential_offers,

    hasAppliedOffers: dto.has_applied_offers,

    creator: dto.creator?.data ? Employee.fromJson(dto.creator.data) : null,

    createdAt: dto.created_at ? DateTime.fromISO(dto.created_at) : null,
    updatedAt: dto.updated_at ? DateTime.fromISO(dto.updated_at) : null,

    remindedAt: dto.reminded_at ? DateTime.fromISO(dto.reminded_at) : null,

    permissions: { canManage: dto.permissions.can_manage },
}))